import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AgreementStateActionType, useAgreementContext } from './index';
import * as css from './agreementdetails.module.scss';
import { FakeLockedInput, TextInput } from '../../components/input';
import DatePicker, { DatePickerConstraints } from '../../components/datepicker';
import translations from './agreementdetails.i18n';
import { formatLocaleNo } from '../../date.utils';
import { PersonDto } from '../../api/generated/studiested-generated-api';
import { devEnvFlag, testEnvFlag } from '../../util/featureFlags';
import dayjs from 'dayjs';

const useAvtalenavnHasChanged = (): ((
  avtalenavn: string | undefined,
) => boolean) => {
  const { initAgreement } = useAgreementContext();

  return (avtalenavn: string | undefined) => initAgreement?.navn !== avtalenavn;
};

const useStartdatoHasChanged = (): ((startdato: string) => boolean) => {
  const { initAgreement } = useAgreementContext();
  const initStartdato = initAgreement?.startdato ?? '';

  return (startdato: string) => initStartdato !== startdato;
};

const useSluttdatoHasChanged = (): ((sluttdato: string) => boolean) => {
  const { initAgreement } = useAgreementContext();
  const initSluttdato = initAgreement?.sluttdato ?? '';

  return (sluttdato: string) => initSluttdato !== sluttdato;
};

const AgreementDetails = () => {
  const {
    navn,
    startdato,
    sluttdato,
    ansvarlig,
    dispatchAgreementEdit,
    canEditAgreement,
  } = useAgreementContext();

  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      navnLabel: formatMessage({ id: translations.navnLabel.id }),
      navnPlaceholder: formatMessage({ id: translations.navnPlaceholder.id }),
      saksnrLabel: formatMessage({ id: translations.saksnrLabel.id }),
      saksnr: formatMessage({ id: translations.saksnr.id }),
      startdato: formatMessage({ id: translations.startdato.id }),
      startdatoPlaceholder: formatMessage({
        id: translations.startdatoPlaceholder.id,
      }),
      sluttdato: formatMessage({ id: translations.sluttdato.id }),
      sluttdatoPlaceholder: formatMessage({
        id: translations.sluttdatoPlaceholder.id,
      }),
      ansvarlig: formatMessage({ id: translations.ansvarlig.id }),
      ingen: formatMessage({ id: translations.ingen.id }),
    }),
    [formatMessage],
  );

  const avtalenavnHasChanged = useAvtalenavnHasChanged();
  const startdatoHasChanged = useStartdatoHasChanged();
  const sluttdatoHasChanged = useSluttdatoHasChanged();

  const setAvtaleNavn = (avtalenavn: string | undefined) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_AVTALENAVN,
      payload: {
        content: avtalenavn,
        avtalenavnHasChanged: avtalenavnHasChanged(avtalenavn),
      },
    });

  const setStartDato = (dateString: string) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_STARTDATO,
      payload: {
        content: dateString,
        startdatoHasChanged: startdatoHasChanged(dateString),
      },
    });

  const setSluttDato = (dateString: string) =>
    dispatchAgreementEdit({
      type: AgreementStateActionType.SET_SLUTTDATO,
      payload: {
        content: dateString,
        sluttdatoHasChanged: sluttdatoHasChanged(dateString),
      },
    });

  const startDateConstraints: DatePickerConstraints = useMemo(
    () => ({ maxDate: sluttdato ? dayjs(sluttdato) : undefined }),
    [sluttdato],
  );
  const toDateConstraints: DatePickerConstraints = useMemo(
    () => ({
      minDate: startdato ? dayjs(startdato) : undefined,
    }),
    [startdato],
  );

  const ansvarligPerson = ansvarlig as PersonDto;

  if (!canEditAgreement) {
    return (
      <div className={css.agreementDetailsWrapper}>
        <FieldRow>
          <FakeLockedInput
            label={labels.navnLabel}
            value={navn || ''}
            containerClassName={css.navnField}
            dataCy={'input-avtalenavn'}
          />
          <FakeLockedInput
            label={labels.saksnrLabel}
            value={labels.saksnr}
            containerClassName={css.saksnrField}
          />
        </FieldRow>
        <FieldRow>
          <div className={css.datoFields}>
            <FakeLockedInput
              label={labels.startdato}
              value={startdato ? formatLocaleNo(startdato) : ''}
              containerClassName={css.datoField}
              dataCy={'input-startdato'}
            />
            <FakeLockedInput
              label={labels.sluttdato}
              value={sluttdato ? formatLocaleNo(sluttdato) : labels.ingen}
              containerClassName={css.datoField}
              dataCy={'input-sluttdato'}
            />
          </div>
          <FakeLockedInput
            label={labels.ansvarlig}
            value={
              ansvarligPerson?.fornavn
                ? `${ansvarligPerson.fornavn} ${ansvarligPerson.etternavn}`
                : (ansvarlig as string | undefined)
            }
            containerClassName={css.ansvarligField}
          />
        </FieldRow>
      </div>
    );
  }

  return (
    <div className={css.agreementDetailsWrapper}>
      <FieldRow>
        <TextInput
          label={labels.navnLabel}
          placeholder={labels.navnPlaceholder}
          htmlId={'input-avtalenavn'}
          onChange={setAvtaleNavn}
          value={navn || ''}
          containerClassName={css.navnField}
          required
        />
        {(devEnvFlag || testEnvFlag) && (
          <FakeLockedInput
            label={labels.saksnrLabel}
            value={labels.saksnr}
            containerClassName={css.saksnrField}
          />
        )}
      </FieldRow>
      <FieldRow>
        <div className={css.datoFields}>
          <DatePicker
            label={labels.startdato}
            placeholder={labels.startdatoPlaceholder}
            dataCy={'input-startdato'}
            onChange={setStartDato}
            value={startdato}
            className={css.datoField}
            constraints={startDateConstraints}
            required
          />
          <DatePicker
            label={labels.sluttdato}
            placeholder={labels.sluttdatoPlaceholder}
            dataCy={'input-sluttdato'}
            onChange={setSluttDato}
            value={sluttdato}
            className={css.datoField}
            constraints={toDateConstraints}
          />
        </div>
        {(devEnvFlag || testEnvFlag) && (
          <FakeLockedInput
            label={labels.ansvarlig}
            value={
              ansvarligPerson?.fornavn
                ? `${ansvarligPerson.fornavn} ${ansvarligPerson.etternavn}`
                : (ansvarlig as string | undefined)
            }
            containerClassName={css.ansvarligField}
          />
        )}
      </FieldRow>
    </div>
  );
};

const FieldRow: React.FC<React.PropsWithChildren> = ({ children }) => (
  <div className={css.row}>{children}</div>
);

export default AgreementDetails;
